import type { JSONValue } from '@/types/json';

export enum ReadyState {
  UNINITIALIZED,
  CONNECTING,
  OPEN,
  READY,
  DISCONNECTING,
  CLOSED,
}

export type Subscription<Type = unknown, K extends string = string, FromSocketType = Type> = (
  event: MessageEvent,
  data: API.SubscriptionResponse<Type, K, FromSocketType>,
) => void;

export type Subscriptions<Tt, FromSocketType> = {
  [K in keyof Tt]?: K extends string ? Subscription<Tt[K], K, FromSocketType> : void;
};

export type WebSocketParams<T, V, FromSocketType> = {
  connect?: boolean;
  onOpen?: (event: Event) => void;
  onReady?: (event: MessageEvent) => void;
  onMessage?: (event: MessageEvent, data?: API.SubscriptionResponse<V, string> | null) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event | Error) => void;
  subscriptions?: Subscriptions<T, FromSocketType>;
};

export type HookReturn<T> = {
  ready: boolean;
  readyState: ReadyState;
  sendMessage: (message: string, keep?: boolean) => void;
  lastMessage: T | null;
  lastMessageRaw: string | ArrayBuffer | null;
  close: () => void;
  reconnect: () => void;
};

export interface Connection<V = JSONValue> {
  socket: WebSocket | null;
  readyState: ReadyState;
  queue: string[];
  baseListeners: Map<
    'message' | 'open' | 'close' | 'error' | 'ready',
    (this: Connection<V>, event: any) => void
  >;
  listeners: Map<
    'message' | 'open' | 'close' | 'error' | 'ready',
    Set<
      (
        this: Connection<V>,
        event: Error | Event | MessageEvent,
        data?: API.SubscriptionResponse<V, string> | null,
      ) => void
    >
  >;
  subscribers: Map<string, Set<Subscription<V>>>;
  timer: Map<any, NodeJS.Timeout> & Map<'subscriptions', Map<any, Set<NodeJS.Timeout>>>;
  delay: number;
  shouldReconnect: boolean;
  mounted: Set<any>;
  close: () => void;
  reconnect: () => void;
  resubscribe: () => void;
  unlisten: (
    handlers: Partial<
      Record<
        'message' | 'open' | 'close' | 'error' | 'ready',
        (this: Connection<V>, e: any) => void
      >
    >,
    subscriptions?: Subscriptions<any, any> | null | undefined,
  ) => void;
  unmount: (
    id: any,
    handlers: Partial<
      Record<
        'message' | 'open' | 'close' | 'error' | 'ready',
        (this: Connection<V>, e: any) => void
      >
    >,
    subscriptions: Subscriptions<any, any> | null | undefined,
    setReadyState: (next: ReadyState) => void,
  ) => void;
  relisten: (
    handlers: Partial<
      Record<
        'message' | 'open' | 'close' | 'error' | 'ready',
        (this: Connection<V>, e: any) => void
      >
    >,
    subscriptions?: Subscriptions<any, any> | null | undefined,
  ) => void;
  mount: (
    id: any,
    handlers: Partial<
      Record<
        'message' | 'open' | 'close' | 'error' | 'ready',
        (this: Connection<V>, e: any) => void
      >
    >,
    subscriptions: Subscriptions<any, any> | null | undefined,
    setReadyState: (next: ReadyState) => void,
  ) => void;
  sendMessage: (message: string, keep?: boolean) => void;
  readyStaters: Set<(next: ReadyState) => void>;
  setReadyState: (next: ReadyState) => void;
}

export interface ConnectionPool<V = JSONValue> {
  [url: string]: Connection<V>;
}
